import { useState } from 'react';
import { Plus, Copy, Eye, EyeOff, Trash2, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';

interface APIKey {
  id: string;
  name: string;
  key: string;
  permissions: string[];
  lastUsed: string;
  createdAt: string;
  expiresAt?: string;
  status: 'active' | 'expired' | 'revoked';
}

interface RateLimit {
  service: string;
  current: number;
  max: number;
  period: string;
  burst: number;
}

// Mock data
const mockAPIKeys: APIKey[] = [
  {
    id: '1',
    name: 'Production Bot Key',
    key: 'sk-proj-abc123...',
    permissions: ['bots:read', 'bots:write', 'metrics:read'],
    lastUsed: '2024-01-24T10:30:00Z',
    createdAt: '2024-01-15T09:00:00Z',
    status: 'active'
  },
  {
    id: '2',
    name: 'Analytics Service',
    key: 'sk-proj-def456...',
    permissions: ['metrics:read', 'costs:read'],
    lastUsed: '2024-01-24T08:15:00Z',
    createdAt: '2024-01-10T14:30:00Z',
    status: 'active'
  },
  {
    id: '3',
    name: 'Legacy Integration',
    key: 'sk-proj-ghi789...',
    permissions: ['bots:read'],
    lastUsed: '2024-01-20T16:45:00Z',
    createdAt: '2024-01-01T12:00:00Z',
    expiresAt: '2024-01-25T12:00:00Z',
    status: 'expired'
  }
];

const mockRateLimits: RateLimit[] = [
  { service: 'OpenRouter', current: 1000, max: 5000, period: 'minute', burst: 100 },
  { service: 'Vapi', current: 100, max: 500, period: 'minute', burst: 50 },
  { service: 'Anthropic', current: 50, max: 200, period: 'minute', burst: 20 }
];

// Hook for API keys
const useAPIKeys = () => ({
  apiKeys: mockAPIKeys,
  loading: false,
  error: null
});

interface APIKeyItemProps {
  apiKey: APIKey;
  onRevoke: () => void;
  onRegenerate: () => void;
}

function APIKeyItem({ apiKey, onRevoke, onRegenerate }: APIKeyItemProps) {
  const [showKey, setShowKey] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      active: 'default',
      expired: 'secondary',
      revoked: 'destructive'
    };
    return variants[status as keyof typeof variants] || 'outline';
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey.key);
    console.log('API key copied to clipboard');
  };

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-sm">{apiKey.name}</div>
          <div className="text-xs text-muted-foreground">
            Created {formatDate(apiKey.createdAt)}
          </div>
        </div>
        <Badge variant={getStatusBadge(apiKey.status) as any}>
          {apiKey.status}
        </Badge>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <code className="text-xs bg-gray-100 px-2 py-1 rounded flex-1">
            {showKey ? apiKey.key : '••••••••••••••••••••••••••••••••'}
          </code>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={() => setShowKey(!showKey)}
          >
            {showKey ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={copyToClipboard}
          >
            <Copy className="w-3 h-3" />
          </Button>
        </div>

        <div className="flex flex-wrap gap-1">
          {apiKey.permissions.map((permission) => (
            <Badge key={permission} variant="outline" className="text-xs">
              {permission}
            </Badge>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Last used: {formatDate(apiKey.lastUsed)}</span>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" className="h-6 px-2" onClick={onRegenerate}>
            <RotateCcw className="w-3 h-3 mr-1" />
            Regenerate
          </Button>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-red-600" onClick={onRevoke}>
            <Trash2 className="w-3 h-3 mr-1" />
            Revoke
          </Button>
        </div>
      </div>

      {apiKey.expiresAt && (
        <div className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
          Expires: {formatDate(apiKey.expiresAt)}
        </div>
      )}
    </div>
  );
}

interface RateLimitConfigProps {
  service: string;
  current: number;
  max: number;
  period: string;
  burst: number;
}

function RateLimitConfig({ service, current, max, period, burst }: RateLimitConfigProps) {
  const [limit, setLimit] = useState(current);
  const [burstLimit, setBurstLimit] = useState(burst);
  
  const percentage = (current / max) * 100;
  
  const getUsageColor = () => {
    if (percentage > 90) return 'text-red-600';
    if (percentage > 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{service}</CardTitle>
          <span className={`text-sm font-mono ${getUsageColor()}`}>
            {current.toLocaleString()} / {max.toLocaleString()} per {period}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs">Rate Limit</Label>
          <Slider
            value={[limit]}
            onValueChange={(value) => setLimit(value[0])}
            max={max}
            step={10}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0</span>
            <span>{limit} requests/{period}</span>
            <span>{max}</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Burst Limit</Label>
          <Slider
            value={[burstLimit]}
            onValueChange={(value) => setBurstLimit(value[0])}
            max={max / 2}
            step={5}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0</span>
            <span>{burstLimit} burst</span>
            <span>{max / 2}</span>
          </div>
        </div>

        <Button size="sm" className="w-full">
          Update Limits
        </Button>
      </CardContent>
    </Card>
  );
}

export function APIConfiguration() {
  const { apiKeys } = useAPIKeys();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newKey, setNewKey] = useState({
    name: '',
    permissions: [] as string[],
    expiresIn: '30'
  });

  const revokeKey = (keyId: string) => {
    console.log('Revoking key:', keyId);
  };

  const regenerateKey = (keyId: string) => {
    console.log('Regenerating key:', keyId);
  };

  const createAPIKey = () => {
    console.log('Creating API key:', newKey);
    setIsCreateDialogOpen(false);
    setNewKey({ name: '', permissions: [], expiresIn: '30' });
  };

  const availablePermissions = [
    'bots:read', 'bots:write', 'bots:delete',
    'metrics:read', 'costs:read', 'logs:read',
    'settings:read', 'settings:write'
  ];

  return (
    <div className="space-y-6">
      {/* API Keys Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>API Keys</CardTitle>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Generate New Key
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Generate API Key</DialogTitle>
                  <DialogDescription>
                    Create a new API key with specific permissions and expiration.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="keyName">Key Name</Label>
                    <Input
                      id="keyName"
                      value={newKey.name}
                      onChange={(e) => setNewKey(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Production Bot Key"
                    />
                  </div>
                  
                  <div>
                    <Label>Permissions</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {availablePermissions.map((permission) => (
                        <label key={permission} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={newKey.permissions.includes(permission)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewKey(prev => ({
                                  ...prev,
                                  permissions: [...prev.permissions, permission]
                                }));
                              } else {
                                setNewKey(prev => ({
                                  ...prev,
                                  permissions: prev.permissions.filter(p => p !== permission)
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm">{permission}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="expiration">Expires In (days)</Label>
                    <Select value={newKey.expiresIn} onValueChange={(value) => setNewKey(prev => ({ ...prev, expiresIn: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                        <SelectItem value="365">1 year</SelectItem>
                        <SelectItem value="never">Never</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={createAPIKey} disabled={!newKey.name || newKey.permissions.length === 0}>
                    Generate Key
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {apiKeys.map((key) => (
              <APIKeyItem
                key={key.id}
                apiKey={key}
                onRevoke={() => revokeKey(key.id)}
                onRegenerate={() => regenerateKey(key.id)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Rate Limits Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Rate Limits</CardTitle>
          <div className="text-sm text-muted-foreground">
            Configure API rate limits per service
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {mockRateLimits.map((limit) => (
              <RateLimitConfig key={limit.service} {...limit} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* API Usage Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>API Usage Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Endpoint</TableHead>
                <TableHead className="text-right">Requests (24h)</TableHead>
                <TableHead className="text-right">Avg Response Time</TableHead>
                <TableHead className="text-right">Error Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>/api/bots</TableCell>
                <TableCell className="text-right font-mono">12,847</TableCell>
                <TableCell className="text-right font-mono">125ms</TableCell>
                <TableCell className="text-right font-mono text-green-600">0.02%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>/api/metrics</TableCell>
                <TableCell className="text-right font-mono">8,932</TableCell>
                <TableCell className="text-right font-mono">89ms</TableCell>
                <TableCell className="text-right font-mono text-green-600">0.01%</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>/api/costs</TableCell>
                <TableCell className="text-right font-mono">4,521</TableCell>
                <TableCell className="text-right font-mono">156ms</TableCell>
                <TableCell className="text-right font-mono text-yellow-600">0.08%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}